<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  maxButtons: { type: Number, default: 10 },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const buttons = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const typeOptions = computed(() => [
  {
    value: 'QUICK_REPLY',
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.TYPES.QUICK_REPLY'),
  },
  { value: 'URL', label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.TYPES.URL') },
  {
    value: 'PHONE_NUMBER',
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.TYPES.PHONE_NUMBER'),
  },
]);

const canAddButton = computed(() => buttons.value.length < props.maxButtons);

const addButton = () => {
  if (!canAddButton.value) return;
  buttons.value = [
    ...buttons.value,
    { type: 'QUICK_REPLY', text: '', url: '', phoneNumber: '', example: '' },
  ];
};

const removeButton = index => {
  buttons.value = buttons.value.filter((_, i) => i !== index);
};

const updateButton = (index, key, value) => {
  buttons.value = buttons.value.map((button, i) =>
    i === index ? { ...button, [key]: value } : button
  );
};

const hasUrlVariable = button =>
  button.type === 'URL' && button.url?.includes('{{');
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      v-for="(button, index) in buttons"
      :key="index"
      class="flex flex-col gap-2 p-3 rounded-lg outline outline-1 outline-n-weak"
    >
      <div class="flex gap-2 items-center">
        <SelectInput
          :model-value="button.type"
          :options="typeOptions"
          class="flex-shrink-0 w-44"
          @update:model-value="value => updateButton(index, 'type', value)"
        />
        <NextButton
          sm
          faded
          ruby
          icon="i-lucide-trash-2"
          class="ms-auto"
          @click="removeButton(index)"
        />
      </div>

      <Input
        :model-value="button.text"
        :placeholder="t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.TEXT_PLACEHOLDER')"
        maxlength="25"
        @update:model-value="value => updateButton(index, 'text', value)"
      />

      <Input
        v-if="button.type === 'URL'"
        :model-value="button.url"
        :placeholder="t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.URL_PLACEHOLDER')"
        @update:model-value="value => updateButton(index, 'url', value)"
      />
      <Input
        v-if="hasUrlVariable(button)"
        :model-value="button.example"
        :placeholder="
          t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.URL_EXAMPLE_PLACEHOLDER')
        "
        @update:model-value="value => updateButton(index, 'example', value)"
      />

      <Input
        v-if="button.type === 'PHONE_NUMBER'"
        :model-value="button.phoneNumber"
        :placeholder="
          t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.PHONE_PLACEHOLDER')
        "
        @update:model-value="value => updateButton(index, 'phoneNumber', value)"
      />
    </div>

    <NextButton
      sm
      faded
      slate
      icon="i-lucide-plus"
      :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.ADD')"
      :disabled="!canAddButton"
      class="self-start"
      @click="addButton"
    />
  </div>
</template>
